import { FC, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Select,
  Spinner,
  Text,
} from "@radix-ui/themes";
import { ErrorFallback } from "@/components";
import { useChallengesBySlugs } from "@/features/challenge/hooks/useChallengesBySlugs";
import { ChallengeCard } from "@/features/challenge";
import { Zones } from "@/models/database/zones/Zones.model";
import DateObject from "react-date-object";

interface SingleEventChallengesProps {
  challenges: string[];
  zone: Zones;
  endDate: DateObject;
  startDate: DateObject;
}

export const SingleEventChallenges: FC<SingleEventChallengesProps> = ({
  challenges,
  zone,
  endDate,
  startDate,
}): JSX.Element => {
  const [selectedStatus, setSelectedStatus] = useState<
    "upcoming" | "ongoing" | "finished"
  >("ongoing");
  const {
    data: relatedChallenges,
    loading,
    error,
  } = useChallengesBySlugs(challenges, selectedStatus);

  const today = new DateObject();

  useEffect(() => {
    if (today > endDate) {
      setSelectedStatus("finished");
    }
    if (today < startDate) {
      setSelectedStatus("upcoming");
    }
  }, []);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorFallback>Failed to load news</ErrorFallback>;
  }

  return (
    <Box>
      <Flex gap="3" justify="between">
        <Heading as="h2" size="4" mb="2">
          Challenges
        </Heading>
        <Select.Root
          defaultValue={selectedStatus}
          onValueChange={(value) =>
            setSelectedStatus(value as "upcoming" | "ongoing" | "finished")
          }
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Item value="upcoming">Upcoming</Select.Item>
            <Select.Item value="ongoing">Ongoing</Select.Item>
            <Select.Item value="finished">Finished</Select.Item>
          </Select.Content>
        </Select.Root>
      </Flex>
      <Grid columns={{ initial: "1", lg: "3" }} gap="5">
        {relatedChallenges.length > 0 ? (
          relatedChallenges.map((challenge, index) => (
            <Box key={index}>
              {/* Render challenge card */}
              <ChallengeCard
                created={challenge.created}
                createdBy={challenge.author?.uid} // Possibility of author profile get deleted
                creatorType={challenge.creatorType}
                fieldToCompare={
                  challenge.challengeAdditionalData.fieldToCompare
                }
                game={challenge.game}
                gameType={challenge.gameType}
                goal={"win"} // Not getting from backend
                logoUrl={challenge.logoUrl}
                name={challenge.name}
                slug={challenge.slug}
                author={challenge.author}
                startDate={{
                  _seconds: challenge.startDate.seconds,
                  _nanoseconds: challenge.startDate.nanoseconds,
                }}
                endDate={{
                  _seconds: challenge.endDate.seconds,
                  _nanoseconds: challenge.endDate.nanoseconds,
                }}
                status={challenge.status}
                zone={zone}
              />
            </Box>
          ))
        ) : (
          <Text>No {selectedStatus} challenges found.</Text>
        )}
      </Grid>
    </Box>
  );
};
